import { useMemo } from "react";
import { useAppSetting } from "@/hooks/useAppSettings";
import { useEquipmentTags, type EquipmentTag } from "@/hooks/useEquipmentTags";

/** Shape stored in app_settings under key "camera_plan_columns" */
export interface CameraPlanEquipmentColumnConfig {
  key: string;
  label: string;
  tagIds: string[];
}

/** Resolved column with expanded tag IDs (including descendants) */
export interface CameraPlanEquipmentColumn {
  key: string;
  label: string;
  tagIds: string[];
}

/**
 * Reads the camera_plan_columns setting and expands each column's tagIds
 * to include all descendant tags, so equipment filtering catches sub-categories.
 */
export function useCameraPlanEquipmentColumns() {
  const { data: config, isLoading: configLoading } = useAppSetting<
    CameraPlanEquipmentColumnConfig[]
  >("camera_plan_columns");
  const { data: tags, isLoading: tagsLoading } = useEquipmentTags();

  const columns = useMemo((): CameraPlanEquipmentColumn[] => {
    if (!Array.isArray(config) || !tags) return [];

    // Build parent → children map
    const childrenMap = new Map<string, EquipmentTag[]>();
    for (const tag of tags) {
      if (tag.parent_id) {
        const siblings = childrenMap.get(tag.parent_id) || [];
        siblings.push(tag);
        childrenMap.set(tag.parent_id, siblings);
      }
    }

    function collectDescendants(tagId: string, result: Set<string>) {
      result.add(tagId);
      for (const child of childrenMap.get(tagId) || []) {
        collectDescendants(child.id, result);
      }
    }

    return config.map((col) => {
      const expanded = new Set<string>();
      for (const id of col.tagIds) {
        collectDescendants(id, expanded);
      }
      return { key: col.key, label: col.label, tagIds: Array.from(expanded) };
    });
  }, [config, tags]);

  return {
    columns,
    isLoading: configLoading || tagsLoading,
  };
}
